import type { Document } from '../types';
import type { Route } from '../types';
import { documentTypeLabel } from './enumUiLabels';
import { resolveDocumentDisplayTypeLabel, resolveDocumentUserFacingFileName } from './documentDisplayName';
import { documentMatchesSearchQuery } from './documentSearch';
import { resolveDocumentFilename } from './documentDownload';
import { filterRouteDocumentsForApplication } from './documentUploadScope';

export type ApplicationDocumentSource = 'Заявка' | 'Маршрут' | 'Погрузка' | 'Выгрузка';

export type ApplicationDocumentRow = {
  id: number;
  createdAt: string;
  fileName: string;
  typeLabel: string;
  documentType: string;
  documentCategory: string;
  source: ApplicationDocumentSource;
  routeLabel: string | null;
  stopLabel: string | null;
  routeId: number | null;
  comment: string | null;
  customDocumentName?: string | null;
  fileType?: string | null;
};

export type ApplicationDocumentFilterDraft = {
  source?: ApplicationDocumentSource | 'all';
  documentCategory?: string;
  routeId?: number;
  stopLabel?: string;
  search?: string;
};

const ROUTE_WIDE_TYPES = new Set(['CARRIER_CONTRACT_REQUEST', 'CARRIER_INVOICE', 'LOADING_INVOICE']);

const ROUTE_OPERATIONAL_TYPES = new Set([
  'LOADING_PHOTO',
  'LOADING_TN',
  'UNLOADING_PHOTO',
  'UNLOADING_DOC',
  'CARRIER_CONTRACT_REQUEST',
  'CARRIER_INVOICE',
  'LOADING_INVOICE',
  'RAIL_WAYBILL',
  'AWB',
  'POWER_OF_ATTORNEY',
  'SCANS_SENT',
]);

const LOADING_STAGE_TYPES = new Set(['LOADING_PHOTO', 'LOADING_TN', 'LOADING_DOCS', 'LOADING_DOCUMENTS', 'LOADING_DOCUMENTS_UPLOAD']);
const UNLOADING_STAGE_TYPES = new Set(['UNLOADING_PHOTO', 'UNLOADING_DOC', 'UNLOADING_DOCS', 'UNLOADING_DOCUMENTS', 'UNLOADING_DOCUMENTS_UPLOAD']);

const CATEGORY_TYPE_MAP: Record<string, string[]> = {
  loading_photo: ['LOADING_PHOTO', 'loading_photo'],
  unloading_photo: ['UNLOADING_PHOTO'],
  loading_docs: ['LOADING_TN', 'LOADING_DOCS', 'LOADING_DOCUMENTS', 'LOADING_DOCUMENTS_UPLOAD'],
  unloading_docs: ['UNLOADING_DOC', 'UNLOADING_DOCS', 'UNLOADING_DOCUMENTS', 'UNLOADING_DOCUMENTS_UPLOAD'],
  tn: ['SIGNED_TN', 'LOADING_TN', 'SCANS_SENT', 'transport_doc'],
  carrier_contract: ['CARRIER_CONTRACT_REQUEST'],
  carrier_invoice: ['CARRIER_INVOICE', 'LOADING_INVOICE', 'loading_invoice'],
  client_docs: ['INVOICE', 'primary_docs'],
  acts: ['UPD_ACT', 'UPD'],
};

function normType(value?: string | null): string {
  return String(value || '').trim().toUpperCase();
}

function isEntityDocument(doc: Document): boolean {
  const t = String(doc.entityType || '').trim().toLowerCase();
  return t === 'driver' || t === 'vehicle';
}

export function resolveApplicationDocumentSource(doc: Document): ApplicationDocumentSource {
  const dt = normType(doc.documentType);
  const stage = String(doc.stage || '').trim().toLowerCase();

  if (ROUTE_WIDE_TYPES.has(dt)) return 'Маршрут';

  if (ROUTE_OPERATIONAL_TYPES.has(dt) || LOADING_STAGE_TYPES.has(dt) || UNLOADING_STAGE_TYPES.has(dt)) {
    if (stage === 'loading' || LOADING_STAGE_TYPES.has(dt)) return 'Погрузка';
    if (stage === 'unloading' || UNLOADING_STAGE_TYPES.has(dt)) return 'Выгрузка';
    if (doc.routeId != null) return 'Маршрут';
  }

  if (doc.applicationId != null && doc.routeId == null) return 'Заявка';
  if (doc.routeId != null) return 'Маршрут';
  if (doc.applicationId != null) return 'Заявка';

  return 'Маршрут';
}

export function resolveApplicationDocumentCategory(doc: Document): string {
  if (doc.isOriginalTN) return 'originals';
  const dt = normType(doc.documentType);
  if (['ORIGINAL_TN', 'TN_ORIGINAL'].includes(dt)) return 'originals';
  if (dt === 'OTHER') {
    const customCat = normType(doc.customDocumentCategory);
    if (customCat === 'CLIENT') return 'client_docs';
    return 'other';
  }

  for (const [category, types] of Object.entries(CATEGORY_TYPE_MAP)) {
    if (types.some((t) => normType(t) === dt)) return category;
  }

  return 'other';
}

export function formatRouteLabel(route: Route | null | undefined, routeId?: number | null): string | null {
  if (!route && routeId == null) return null;
  const id = route?.id ?? routeId;
  if (id == null) return null;
  const from = route?.loadingAddress || route?.loadingCity;
  const to = route?.unloadingAddress || route?.unloadingCity;
  if (from && to) return `#${id} ${from} → ${to}`;
  return `#${id}`;
}

export function resolveStopLabel(doc: Document, route?: Route | null): string | null {
  const source = resolveApplicationDocumentSource(doc);
  if (source !== 'Погрузка' && source !== 'Выгрузка') return null;

  const stage = String(doc.stage || '').trim().toLowerCase();
  if (stage === 'loading' || source === 'Погрузка') {
    return route?.loadingAddress || route?.loadingCity || 'Погрузка';
  }
  if (stage === 'unloading' || source === 'Выгрузка') {
    return route?.unloadingAddress || route?.unloadingCity || 'Выгрузка';
  }
  return source;
}

export function documentToApplicationRow(doc: Document, routesById: Map<number, Route>): ApplicationDocumentRow {
  const routeId = doc.routeId ?? doc.route?.id ?? null;
  const route = routeId != null ? routesById.get(routeId) ?? doc.route ?? null : null;
  const source = resolveApplicationDocumentSource(doc);

  return {
    id: doc.id,
    createdAt: doc.createdAt,
    fileName: (() => {
      const display = resolveDocumentUserFacingFileName({
        documentType: doc.documentType,
        customDocumentName: doc.customDocumentName,
        fileName: resolveDocumentFilename(doc),
        content: doc.content,
      });
      return display !== '—' ? display : resolveDocumentFilename(doc);
    })(),
    typeLabel: resolveDocumentDisplayTypeLabel(doc, documentTypeLabel),
    documentType: doc.documentType,
    documentCategory: resolveApplicationDocumentCategory(doc),
    source,
    routeLabel: source === 'Заявка' ? null : formatRouteLabel(route, routeId),
    stopLabel: source === 'Заявка' || source === 'Маршрут' ? null : resolveStopLabel(doc, route),
    routeId,
    comment: doc.comment ?? null,
    customDocumentName: doc.customDocumentName ?? null,
    fileType: doc.fileType,
  };
}

export function mergeApplicationDocuments(
  applicationId: number,
  applicationDocuments: Document[] | undefined,
  routeDocumentsByRouteId: Map<number, Document[]>,
  routes: Route[]
): ApplicationDocumentRow[] {
  const routesById = new Map<number, Route>(routes.map((r) => [r.id, r]));
  const seen = new Set<number>();
  const merged: Document[] = [];

  for (const doc of applicationDocuments ?? []) {
    if (isEntityDocument(doc)) continue;
    if (seen.has(doc.id)) continue;
    seen.add(doc.id);
    merged.push(doc);
  }

  for (const route of routes) {
    const routeDocs = filterRouteDocumentsForApplication(routeDocumentsByRouteId.get(route.id), applicationId);
    for (const doc of routeDocs) {
      if (isEntityDocument(doc)) continue;
      if (seen.has(doc.id)) continue;
      seen.add(doc.id);
      merged.push(doc);
    }
  }

  return merged
    .map((doc) => documentToApplicationRow(doc, routesById))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

function matchesCategory(row: ApplicationDocumentRow, category?: string): boolean {
  if (!category) return true;
  if (category === 'other') return row.documentCategory === 'other';
  if (category === 'originals') return row.documentCategory === 'originals';
  return row.documentCategory === category;
}

export function filterApplicationDocumentRows(
  rows: ApplicationDocumentRow[],
  filters: ApplicationDocumentFilterDraft
): ApplicationDocumentRow[] {
  return rows.filter((row) => {
    if (filters.source && filters.source !== 'all' && row.source !== filters.source) return false;
    if (!matchesCategory(row, filters.documentCategory)) return false;
    if (filters.routeId != null && row.routeId !== filters.routeId) return false;
    if (filters.stopLabel && row.stopLabel !== filters.stopLabel) return false;
    return documentMatchesSearchQuery(
      {
        typeLabel: row.typeLabel,
        fileName: row.fileName,
        customDocumentName: row.customDocumentName,
        comment: row.comment,
        documentType: row.documentType,
      },
      String(filters.search || '')
    );
  });
}

export function collectStopFilterOptions(rows: ApplicationDocumentRow[]): string[] {
  const labels = new Set<string>();
  for (const row of rows) {
    if (row.stopLabel) labels.add(row.stopLabel);
  }
  return Array.from(labels).sort((a, b) => a.localeCompare(b, 'ru'));
}
